/**
 * The unified setup list's steps (checklist-2026-11): one table of copy, icon
 * and action per step, read by both the dashboard "Get set up" list and the
 * compact rail panel, so the two can never disagree.
 *
 * Which steps a person gets, and in what order, is decided server-side at
 * enrolment (`resolveUnifiedChecklistSteps` in `@bower/shared`) and arrives
 * stamped in `unified.coreSteps` / `unified.bonusSteps`. This file only says
 * how each one reads. Copy follows docs/tone-of-voice.md.
 */

import { CHECKLIST_READ_ONLY_STEPS } from '@bower/shared/types/onboardingChecklist';
import type { ChecklistStepKey, ChecklistVariant } from '@bower/shared';

/** What clicking a step does; resolved by the surface that renders it. */
export type ChecklistStepAction =
  | 'ask'
  | 'start_here'
  | 'import'
  | 'run_protocol'
  | 'equipment'
  | 'phone'
  | 'connect'
  | 'invite';

export interface ChecklistStepView {
  readonly key: ChecklistStepKey;
  readonly iconName: string;
  readonly title: string;
  readonly description: string;
  readonly ctaLabel: string;
  readonly action: ChecklistStepAction;
}

type StepCopy = Omit<ChecklistStepView, 'key'>;

const STEPS: Partial<Record<ChecklistStepKey, (variant: ChecklistVariant) => StepCopy>> = {
  ask_bird: (variant) => ({
    iconName: 'chat',
    title: variant === 'owner' ? 'Ask Bower a question' : 'Ask Bower about this workspace',
    description:
      variant === 'owner'
        ? 'Bower reads everything here, so ask it about your protocol or your field.'
        : 'Bower has read what your team keeps here. Ask it where things are.',
    ctaLabel: 'Open chat',
    action: 'ask',
  }),
  open_start_here: (variant) => ({
    iconName: 'flag',
    title: variant === 'owner' ? 'Open Start here' : 'See what your team uses',
    description:
      variant === 'owner'
        ? 'Your welcome note and an example protocol, made for your field.'
        : 'Open the protocol or note your team works from most.',
    ctaLabel: 'Open',
    action: 'start_here',
  }),
  import_first_file: (variant) => ({
    iconName: 'upload_file',
    title: variant === 'owner' ? 'Import your work' : 'Import your own work',
    description:
      'Word docs, PDFs and instrument exports. Bower answers better with your real data.',
    ctaLabel: 'Import',
    action: 'import',
  }),
  run_protocol: () => ({
    iconName: 'play_circle',
    title: 'Run a protocol',
    description: 'Step through a method at the bench, hands-free on your phone.',
    ctaLabel: 'Choose a protocol',
    action: 'run_protocol',
  }),
  capture_equipment: () => ({
    iconName: 'biotech',
    title: 'Add equipment',
    description: 'Photograph an instrument. Bower reads its manual so it can help you run it.',
    ctaLabel: 'Add equipment',
    action: 'equipment',
  }),
  mobile_capture: () => ({
    iconName: 'smartphone',
    title: 'Capture on your phone',
    description: 'Snap your notebook or the bench and Bower reads it in seconds.',
    ctaLabel: 'Show QR',
    action: 'phone',
  }),
  connect_source: () => ({
    iconName: 'hub',
    title: 'Connect a source',
    description: 'Link Drive, OneNote or Sheets so Bower reads across the tools you use.',
    ctaLabel: 'Connect',
    action: 'connect',
  }),
  invite_teammate: () => ({
    iconName: 'group_add',
    title: 'Invite a teammate',
    description: 'Share this workspace so Bower works across your team’s research.',
    ctaLabel: 'Invite',
    action: 'invite',
  }),
};

/** What the reader and the workspace allow, for {@link checklistStepViews}. */
export interface ChecklistStepContext {
  readonly canWrite: boolean;
  /** The Connectors settings tab does not exist in a PHI workspace. */
  readonly isPhi: boolean;
  /** Start here has something this reader can open. */
  readonly hasStartHere: boolean;
}

/**
 * The view of each stamped step, in order, for both surfaces. Dropped rather
 * than shown: a step this bundle has no copy for; for a reader who cannot
 * write here, every step a read-only seat cannot do (the server's own rule,
 * `CHECKLIST_READ_ONLY_STEPS`), whatever was stamped; Connect in a PHI
 * workspace; Start here with nothing to open.
 */
export function checklistStepViews(
  keys: readonly ChecklistStepKey[],
  variant: ChecklistVariant,
  { canWrite, isPhi, hasStartHere }: ChecklistStepContext,
): ChecklistStepView[] {
  return keys.flatMap((key) => {
    const copy = STEPS[key]?.(variant);
    if (!copy) return [];
    if (!canWrite && !CHECKLIST_READ_ONLY_STEPS.has(key)) return [];
    if (isPhi && copy.action === 'connect') return [];
    if (!hasStartHere && copy.action === 'start_here') return [];
    return [{ key, ...copy }];
  });
}
